import { Link, useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { CheckCircle2, MailX, AlertTriangle, Building2 } from 'lucide-react';
import { supabase } from '@/lib/supabase';

type Status = 'confirmado' | 'cancelado' | 'erro';

const CONTENT: Record<Status, { icon: typeof CheckCircle2; iconClass: string; title: string; message: string }> = {
  confirmado: {
    icon: CheckCircle2,
    iconClass: 'text-emerald-500',
    title: 'Inscrição confirmada!',
    message: 'Obrigado — você vai receber nossas próximas novidades por email.',
  },
  cancelado: {
    icon: MailX,
    iconClass: 'text-muted',
    title: 'Inscrição cancelada',
    message: 'Você não vai mais receber nossos emails. Sentiremos sua falta!',
  },
  erro: {
    icon: AlertTriangle,
    iconClass: 'text-danger',
    title: 'Não foi possível processar agora',
    message: 'Algo deu errado do nosso lado. Tente novamente em alguns minutos.',
  },
};

function isPlatformHost(hostname: string) {
  const platformHostname = (import.meta.env.VITE_PLATFORM_HOSTNAME as string | undefined) ?? 'lumenise.app';
  return hostname === platformHostname || hostname === 'localhost' || hostname === '127.0.0.1' || hostname.endsWith('.pages.dev');
}

export function NewsletterStatusPage() {
  const { slug, status = 'erro' } = useParams<{ slug?: string; status?: string }>();
  const hostname = window.location.hostname.toLowerCase();
  const resolveByHost = !slug && !isPlatformHost(hostname);

  const { data: hostSite } = useQuery({
    queryKey: ['newsletter-status-site-host', hostname],
    queryFn: async () => {
      const { data, error } = await supabase.rpc('resolve_site_by_hostname', { input_hostname: hostname });
      if (error) throw error;
      return data?.[0] ?? null;
    },
    enabled: resolveByHost,
  });
  const effectiveSlug = slug ?? hostSite?.slug;
  const { data: site } = useQuery({
    queryKey: ['newsletter-status-site', effectiveSlug],
    queryFn: async () => {
      if (!effectiveSlug) return null;
      const { data, error } = await supabase.from('sites').select('name, slug, site_branding(app_name, logo_url)').eq('slug', effectiveSlug).eq('active', true).maybeSingle();
      if (error) throw error;
      return data;
    },
    enabled: !!effectiveSlug,
  });

  const branding = site?.site_branding as { app_name?: string; logo_url?: string | null } | null;
  const siteName = branding?.app_name || site?.name || 'Lumenise';
  const rootHref = effectiveSlug && isPlatformHost(hostname) ? `/s/${effectiveSlug}` : '/';
  const content = CONTENT[status as Status] ?? CONTENT.erro;
  const Icon = content.icon;

  return (
    <div className="flex min-h-screen items-center justify-center bg-surface px-6 py-12">
      <div className="w-full max-w-sm rounded-xl border border-border bg-card p-8 text-center shadow-sm">
        <Link to={rootHref} className="mb-6 flex items-center justify-center gap-2 font-display text-base font-bold text-ink">
          {branding?.logo_url ? <img src={branding.logo_url} alt="" className="h-7 max-w-32 object-contain" /> : <Building2 className="h-5 w-5 text-accent" />}
          <span className="truncate">{siteName}</span>
        </Link>
        <Icon className={`mx-auto mb-3 h-10 w-10 ${content.iconClass}`} />
        <h1 className="font-display text-xl font-bold text-ink">{content.title}</h1>
        <p className="mt-2 text-sm leading-relaxed text-muted">{content.message}</p>
        <Link to={rootHref} className="mt-6 inline-block text-sm font-medium text-accent hover:underline">
          Voltar ao site
        </Link>
      </div>
    </div>
  );
}
